import type { AxiosError } from 'axios';
import { formatDistanceStrict, formatDuration, format } from 'date-fns';

import type { Positionable } from 'vms/common/types';

export function upsertById<T extends { id: string }>(list: T[], saved: T): T[] {
  return list.some((item) => item.id === saved.id)
    ? list.map((item) => (item.id === saved.id ? saved : item))
    : [...list, saved];
}

export function parseDateTime(dt: string, nowOnError: false): Date | null;
export function parseDateTime(dt: string, nowOnError?: true): Date;
export function parseDateTime(dt: string, nowOnError = true): Date | null {
  const d = Date.parse(
    /^\d{4}-\d{2}-\d{2}$/.test(dt)
      ? `${dt}T00:00:00` // ISO formatted date from Django, JS interpret as local midnight
      : dt
  );
  if (Number.isNaN(d)) {
    return nowOnError ? new Date() : null;
  }
  return new Date(d);
}

export const timeAgo = (dt: string, now?: string) =>
  formatDistanceStrict(now ? parseDateTime(now) : new Date(), parseDateTime(dt));

export const formatDetailedElapsedSeconds = (
  seconds: number | null | undefined
): string => {
  if (seconds === null || seconds === undefined) return '—';

  // Round once before splitting so minutes carry into larger units.
  let minutes = Math.round(seconds / 60);
  if (minutes === 0) return formatDuration({ minutes: 0 }, { zero: true });

  // Fixed 24-hour days keep elapsed time independent of dates/DST.
  const days = Math.floor(minutes / (24 * 60));
  minutes %= 24 * 60;
  const hours = Math.floor(minutes / 60);
  minutes %= 60;

  return formatDuration({ days, hours, minutes });
};

export const formatDate = (dt: Date, dateOnly = false) =>
  dateOnly ? format(dt, 'MM/dd/yyyy') : format(dt, 'MM/dd/yyyy HH:mm');

export const reformatDTZ = (dt: string) => format(parseDateTime(dt), 'MM/dd/yyyy HH:mm');

export const isoFormat = (dt: Date, dateOnly = false) => {
  return dateOnly
    ? format(dt, 'yyyy-MM-dd')
    : format(dt, 'yyyy-MM-dd') + 'T' + format(dt, 'HH:mm:ss');
};

export class BadRequestError {
  message: string;
  data: object;
  code = 'BAD_REQUEST_ERROR';
  name = 'BAD_REQUEST_ERROR';
  originalError: AxiosError;

  constructor(data: object, error: AxiosError) {
    this.message = 'Bad request error.';
    this.data = data;
    this.originalError = error;
  }
}

/**
 * Change positions of items in map.
 *
 * @param items Items to reorder
 * @param newItem Item with new position
 */
export const reorder = (
  items: { [id: string]: Positionable },
  newItem: Positionable
): void => {
  const oldItem = items[newItem.id];
  let minPosition: number;
  let maxPosition: number;
  let sign: number;
  if (oldItem.position < newItem.position) {
    [minPosition, maxPosition] = [oldItem.position, newItem.position];
    sign = -1;
  } else if (newItem.position < oldItem.position) {
    [minPosition, maxPosition] = [newItem.position, oldItem.position];
    sign = 1;
  } else {
    return;
  }
  Object.keys(items)
    .filter(
      (id) => items[id].position >= minPosition && items[id].position <= maxPosition
    )
    .forEach((id) => {
      if (items[id].position === oldItem.position) {
        items[id] = { ...items[id], position: newItem.position };
      } else {
        items[id] = { ...items[id], position: items[id].position + sign };
      }
    });
};
